import { AdminMessage } from "@components/atoms/AdminMessage"
import { BackButton } from "@components/atoms/BackButton"
import { Button } from "@components/atoms/Button"
import { ColourTheme } from "@components/atoms/ColourTheme"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Appear } from "@components/motion/Appear"
import { parseDate } from "@utils/dates"
import { getTranslation } from "@utils/Translations"
import { cn } from "@utils/tw"
import {
  defineBlock,
  EditableText,
  InnerBlocks,
  useBlockContext,
  useInlineEditableValue,
  usePostTitleEditor,
} from "eddev/blocks"
import { Link } from "eddev/routing"
import { useEffect, useState } from "react"

export const meta: BlockMeta = {
  title: "Event Header",
  postMetaBlock: {
    postTypes: ["events"],
    fieldName: "headerData",
  },
}

export default defineBlock("section/event-header", (props) => {
  const [venue, setVenue] = useInlineEditableValue("venue", "")
  const [about, setAbout] = useInlineEditableValue("about", "")
  const showVenue = env.admin || venue !== ""
  const showAbout = env.admin || about !== ""
  const [hasForm, setHasForm] = useState(false)

  useEffect(() => {
    setHasForm(!!document.querySelector(".event-form"))
  }, [])

  const scrollToForm = () => {
    const formEl = document.querySelector(".event-form")
    if (formEl) {
      formEl.scrollIntoView({ behavior: "smooth" })
    }
  }

  return (
    <Appear className={cn("event-header contents relative")}>
      <ColourTheme
        theme={props.colourTheme || props.defaultColour}
        className="absolute w-full top-0 left-0 h-navBarInnerTopBase"
      />
      <ColourTheme theme={props.colourTheme || props.defaultColour} className="w-full relative -mt-1">
        <div className="pb-9 md:py-9 px-6 w-maxContentW mx-auto flex flex-col gap-7 md:gap-4">
          <BackButton href="/events">{getTranslation("backToEvents", "Back to events")}</BackButton>
          <div className="w-full max-w-blogPostVideoW mx-auto flex flex-col gap-8 md:gap-7 items-center">
            <h1 className="type-header-2-xl">
              {env.admin ? (
                <EditableText store={usePostTitleEditor()} as="span" placeholder="Enter title" />
              ) : (
                <>{props.event?.title}</>
              )}
            </h1>
            <div className="w-full flex flex-col md:flex-row gap-8 md:gap-7 items-center justify-between">
              <div className="w-eventHeaderImageW shrink-0">
                {props.event?.featuredImage?.node ? (
                  <ResponsiveImage
                    {...props.event.featuredImage.node}
                    className="w-full h-auto rounded-md overflow-hidden"
                  />
                ) : (
                  <AdminMessage>
                    Set a featured image for this event.
                    <br />
                    Refresh once added to view here.
                  </AdminMessage>
                )}
              </div>
              <div className="w-full flex flex-col gap-4">
                {(env.admin || props.eventDateStart) && (
                  <div className="flex flex-col gap-4 md:gap-3 pb-3 md:pb-2 border-b border-current">
                    <p className="type-ui-label my-0">{getTranslation("date", "Date")}</p>
                    <p className="type-body-s my-0">
                      {parseDate(props.eventDateStart)?.toLocaleDateString(getTranslation("locale", "en-US"), {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                      {props.eventDateEnd &&
                        ` - ${parseDate(props.eventDateEnd)?.toLocaleDateString(getTranslation("locale", "en-US"), {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })}`}
                      {!props.eventDateStart && env.admin && "Enter event date(s) in the side panel"}
                    </p>
                  </div>
                )}
                {showVenue && (
                  <div className="flex flex-col gap-4 md:gap-3 pb-3 md:pb-2 border-b border-current">
                    <p className="type-ui-label my-0">{getTranslation("venue", "Venue")}</p>
                    <EditableText
                      as="p"
                      store="venue"
                      className="type-body-s my-0"
                      placeholder="Enter venue details here"
                    />
                  </div>
                )}
                {showAbout && (
                  <div className="flex flex-col gap-4 md:gap-3 pb-3 md:pb-2">
                    <p className="type-ui-label my-0">{getTranslation("about", "About")}</p>
                    <EditableText
                      as="p"
                      store="about"
                      className="type-body-s my-0"
                      placeholder="Enter a short description about the event here"
                    />
                  </div>
                )}
                {(hasForm || env.admin) && (
                  <Button dark className="mt-3 md:mt-2">
                    <EditableText
                      as="span"
                      store="registerButton"
                      placeholder={getTranslation("meetWithUs", "Meet with us")}
                      defaultValue={getTranslation("meetWithUs", "Meet with us")}
                      onClick={() => scrollToForm()}
                    />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </ColourTheme>
    </Appear>
  )
})
